/**
 * DailyEntryCard Component
 * Displays a single diary entry with expandable content and photo grid
 */

import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  IconButton,
  Collapse,
  ImageList,
  ImageListItem,
  Dialog
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { isVideoUrl } from '../../utils/fileValidation';

const DailyEntryCard = ({ entry, onPhotoClick }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    if (onPhotoClick) {
      onPhotoClick(photoUrl);
    }
  };

  const handleClosePhoto = () => {
    setSelectedPhoto(null);
  };

  // Get milestone color
  const getMilestoneColor = () => {
    if (!entry.milestone_id) return '#ccc';

    switch (entry.milestone_status) {
      case 'completed':
        return '#4CAF50';
      case 'in_progress':
        return '#2196F3';
      case 'pending_verification':
        return '#FF9800';
      case 'overdue':
        return '#F44336';
      default:
        return '#9C27B0';
    }
  };

  // Format relative time
  const getRelativeTime = () => {
    try {
      const entryDate = new Date(entry.entry_date);
      return formatDistanceToNow(entryDate, { addSuffix: true });
    } catch (error) {
      return entry.days_ago ? `${entry.days_ago} days ago` : 'Recently';
    }
  };

  // Format date
  const getFormattedDate = () => {
    try {
      const entryDate = new Date(entry.entry_date);
      return format(entryDate, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      return entry.entry_date;
    }
  };

  // Truncate description for preview
  const getDescriptionPreview = () => {
    if (expanded || !entry.description) return entry.description;
    return entry.description.length > 150
      ? `${entry.description.substring(0, 150)}...`
      : entry.description;
  };

  // Determine how many photos to show in preview
  const photoUrls = entry.photo_urls || [];
  const previewPhotoCount = 4;
  const hasMorePhotos = photoUrls.length > previewPhotoCount;
  const displayPhotos = expanded ? photoUrls : photoUrls.slice(0, previewPhotoCount);

  return (
    <>
      <Paper
        elevation={expanded ? 3 : 1}
        sx={{
          mb: 2,
          cursor: 'pointer',
          borderLeft: '4px solid',
          borderLeftColor: getMilestoneColor(),
          transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            elevation: 3,
            boxShadow: 3
          }
        }}
        onClick={handleToggleExpand}
      >
        <Box sx={{ p: 2 }}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar
                src={entry.contractor_avatar}
                alt={entry.contractor_name}
                sx={{ width: 48, height: 48 }}
              >
                {entry.contractor_name?.charAt(0) || 'C'}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {getFormattedDate()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    by {entry.contractor_name}
                  </Typography>
                  <Chip
                    label={entry.author_role === 'project_manager' ? 'Project Manager' : 'Contractor'}
                    size="small"
                    variant="outlined"
                    color={entry.author_role === 'project_manager' ? 'info' : 'default'}
                    sx={{ height: 20, fontSize: '0.68rem' }}
                  />
                  <TimeIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {getRelativeTime()}
                  </Typography>
                </Box>
              </Box>

              {entry.milestone_name && (
                <Chip
                  label={entry.milestone_name}
                  size="small"
                  sx={{
                    backgroundColor: getMilestoneColor(),
                    color: '#fff',
                    fontWeight: 500
                  }}
                />
              )}
            </Box>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}
          >
            {getDescriptionPreview()}
          </Typography>

          {/* Photo Grid */}
          {displayPhotos.length > 0 && (
            <Box sx={{ position: 'relative' }}>
              <ImageList
                cols={Math.min(displayPhotos.length, 4)}
                gap={8}
                sx={{
                  mb: 0,
                  overflow: 'hidden',
                  height: 'auto'
                }}
              >
                {displayPhotos.map((mediaUrl, index) => (
                  <ImageListItem
                    key={index}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePhotoClick(mediaUrl);
                    }}
                  >
                    {isVideoUrl(mediaUrl) ? (
                      <>
                        <video
                          src={mediaUrl}
                          preload="metadata"
                          muted
                          playsInline
                          style={{
                            height: '150px',
                            width: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            backgroundColor: '#000'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#fff',
                            backgroundColor: 'rgba(0,0,0,0.55)',
                            borderRadius: '50%',
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none'
                          }}
                        >
                          <PlayArrowIcon />
                        </Box>
                      </>
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={`Progress media ${index + 1}`}
                        loading="lazy"
                        style={{
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                  </ImageListItem>
                ))}
              </ImageList>

              {!expanded && hasMorePhotos && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    +{photoUrls.length - previewPhotoCount} more
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Edit indicator */}
          {entry.can_edit && (
            <Collapse in={expanded}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderTopColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  ✏️ You can edit or delete this entry (24 hours from creation)
                </Typography>
              </Box>
            </Collapse>
          )}
        </Box>
      </Paper>

      {/* Photo Lightbox Dialog */}
      <Dialog
        open={!!selectedPhoto}
        onClose={handleClosePhoto}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={handleClosePhoto}
        >
          {selectedPhoto && isVideoUrl(selectedPhoto) ? (
            <video
              src={selectedPhoto}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          ) : (
            <img
              src={selectedPhoto}
              alt="Full size"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default DailyEntryCard;
